import {
  QueryClient,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  InfiniteData,
} from "@tanstack/react-query";
import announcementsService from "../services";
import {
  ICreateAnnouncement,
  ICreateReminder,
  IGetAnnouncementsParams,
} from "../types";
import { BaseGetRequestParams, BaseGetRequestResponse } from "@quest/types";
import { INotificationItem } from "../types";
import { useAppSelector } from "@quest/hooks";

const PREVIEW_REJECT = () => Promise.reject(new Error("Read-only preview"));

export const refreshNotificationsCache = async (queryClient: QueryClient) => {
  await queryClient.cancelQueries({ queryKey: ["notifications"] });
  await queryClient.resetQueries({ queryKey: ["notifications"] });
};

export const useGetAnnouncements = (
  params: IGetAnnouncementsParams,
  enabled: boolean
) => {
  return useQuery({
    queryKey: ["announcements", params],
    queryFn: () => announcementsService.getAnnouncements(params),
    enabled: enabled,
  });
};

export const useGetNotificationsByUser = (
  params: BaseGetRequestParams,
  enabled: boolean,
  locale: string
) => {
  return useInfiniteQuery({
    queryKey: ["notifications", params],
    queryFn: ({ pageParam }) => {
      return announcementsService.getNotificationsByUser(
        {
          ...params,
          page: pageParam as number,
        },
        locale
      );
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      // compute next page based on totalPages
      return lastPage.data.page < lastPage.data.totalPages
        ? lastPage.data.page + 1
        : undefined;
    },
    enabled: enabled,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
};

export const useCreateAnnouncement = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["create-announcement"],
    mutationFn: (data: ICreateAnnouncement) =>
      announcementsService.createAnnouncement(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["announcements"] });
    },
  });
};

export const useGetAnnouncementDetails = (id: number, enabled: boolean) => {
  return useQuery({
    queryKey: ["announcement-details", id],
    queryFn: () => announcementsService.getAnnouncementDetails(id),
    enabled: !!id && enabled,
  });
};

export const usePublishAnnouncement = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["publish-announcement"],
    mutationFn: (id: number) => announcementsService.publishAnnouncement(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["announcement-details"] });
      queryClient.invalidateQueries({ queryKey: ["announcements"] });
    },
  });
};

export const useUpdateAnnouncement = () => {
  return useMutation({
    mutationKey: ["update-announcement"],
    mutationFn: ({ data, id }: { data: ICreateAnnouncement; id: number }) =>
      announcementsService.updateAnnouncement(data, id),
  });
};

export const useDeleteAnnouncement = () => {
  // const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["delete-announcement"],
    mutationFn: (id: number) => announcementsService.deleteAnnouncement(id),
    // onSuccess: () => {
    //   queryClient.invalidateQueries({ queryKey: ["announcements"] });
    // },
  });
};

export const useMarkAllNotificationsAsRead = () => {
  const queryClient = useQueryClient();
  const isPreviewMode = useAppSelector((state) =>
    Boolean(state.preview?.active)
  );
  return useMutation({
    mutationKey: ["mark-all-notifications-as-read"],
    mutationFn: () =>
      isPreviewMode
        ? PREVIEW_REJECT()
        : announcementsService.markAllNotificationsAsRead(),
    onSuccess: () => {
      // Optimistically update all notifications to mark them as read
      // Use setQueriesData to update all matching notification queries
      queryClient.setQueriesData<
        InfiniteData<BaseGetRequestResponse<INotificationItem>>
      >({ queryKey: ["notifications"] }, (oldData) => {
        if (!oldData) return oldData;

        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            data: {
              ...page.data,
              data: page.data.data.map((notification) => ({
                ...notification,
                is_read: true,
              })),
            },
          })),
        };
      });

      // Update unread count to 0
      queryClient.setQueryData<{ data: number }>(
        ["notification-unread-count"],
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            data: 0,
          };
        }
      );

      queryClient.invalidateQueries({
        queryKey: ["notification-unread-count"],
      });
    },
  });
};

export const useMarkNotificationAsRead = () => {
  const queryClient = useQueryClient();
  const isPreviewMode = useAppSelector((state) =>
    Boolean(state.preview?.active)
  );
  return useMutation({
    mutationKey: ["mark-notification-as-read"],
    mutationFn: (id: number) =>
      isPreviewMode
        ? PREVIEW_REJECT()
        : announcementsService.markNotificationAsRead(id),
    onSuccess: (_, notificationId) => {
      // Optimistically update the specific notification to mark it as read
      // Use setQueriesData to update all matching notification queries
      queryClient.setQueriesData<
        InfiniteData<BaseGetRequestResponse<INotificationItem>>
      >({ queryKey: ["notifications"] }, (oldData) => {
        if (!oldData) return oldData;

        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            data: {
              ...page.data,
              data: page.data.data.map((notification) =>
                notification.id === notificationId
                  ? { ...notification, is_read: true }
                  : notification
              ),
            },
          })),
        };
      });

      // Decrease unread count by 1
      queryClient.setQueryData<{ data: number }>(
        ["notification-unread-count"],
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            data: Math.max(0, oldData.data - 1),
          };
        }
      );

      queryClient.invalidateQueries({
        queryKey: ["notification-unread-count"],
      });
    },
  });
};

export const useGetNotificationDetails = (id: number, enabled: boolean) => {
  return useQuery({
    queryKey: ["notification-details", id],
    queryFn: () => announcementsService.getNotificationDetails(id),
    enabled: !!id && enabled,
  });
};

export const useCreateReminder = () => {
  return useMutation({
    mutationKey: ["create-reminder"],
    mutationFn: (data: ICreateReminder) =>
      announcementsService.createReminder(data),
  });
};

export const useGetReminders = (
  params: BaseGetRequestParams,
  enabled: boolean
) => {
  return useQuery({
    queryKey: ["reminders", params],
    queryFn: () => announcementsService.getReminder(params),
    enabled: enabled,
  });
};

export const useGetReminderDetails = (id: number, enabled: boolean) => {
  return useQuery({
    queryKey: ["reminder-details", id],
    queryFn: () => announcementsService.getReminderDetails(id),
    enabled: !!id && enabled,
  });
};

export const useDeleteReminder = () => {
  return useMutation({
    mutationKey: ["delete-reminder"],
    mutationFn: (id: number) => announcementsService.deleteReminder(id),
  });
};

export const useUpdateReminder = () => {
  return useMutation({
    mutationKey: ["update-reminder"],
    mutationFn: ({ data, id }: { data: ICreateReminder; id: number }) =>
      announcementsService.updateReminder(data, id),
  });
};

export const useGetReminderNotificationDetails = (
  id: number,
  enabled: boolean
) => {
  return useQuery({
    queryKey: ["reminder-notification-details", id],
    queryFn: () => announcementsService.getReminderNotificationDetails(id),
    enabled: !!id && enabled,
  });
};
